import { getValidToken } from '../helpers/keycloak';

export class ApiError extends Error {
  constructor(message, { status = null, kind = 'http', phase = null, timings = null } = {}) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.kind = kind;
    this.phase = phase;
    this.timings = timings;
  }
}

const SLOW_REQUEST_MS = 1000;

// Diagnóstico de timeout: registra apenas rota e tempos, nunca token ou payload.
function reportSlowRequest(path, error) {
  if (error.kind !== 'timeout') return;
  if ((error.timings?.totalMs ?? 0) < SLOW_REQUEST_MS) return;

  console.warn('[api] request timed out', {
    path,
    phase: error.phase,
    ...error.timings,
  });
}

export async function apiRequest(
  path,
  { method = 'GET', params = {}, timeoutMs = 30000, signal } = {}
) {
  const baseUrl = process.env.REACT_APP_ELASTIC_API_URL;
  const query = new URLSearchParams(params).toString();
  const url = `${baseUrl}${path}${query ? `?${query}` : ''}`;
  const controller = new AbortController();
  const requestSignal = controller.signal;
  const abortFromCaller = () => controller.abort();
  if (signal?.aborted) {
    controller.abort();
  } else {
    signal?.addEventListener('abort', abortFromCaller, { once: true });
  }
  // Separa as fases para distinguir lentidão do SSO, da API e do parsing.
  const startedAt = performance.now();
  const elapsed = () => Math.round(performance.now() - startedAt);
  const timings = {};
  let phase = 'token';
  let timedOut = false;
  let rejectAbort;
  const abortPromise = new Promise((_, reject) => {
    rejectAbort = reject;
  });
  const handleAbort = () => {
    rejectAbort(new ApiError(
      timedOut ? 'API request timed out' : 'API request cancelled',
      {
        kind: timedOut ? 'timeout' : 'cancelled',
        phase,
        timings: { ...timings, totalMs: elapsed() },
      }
    ));
  };
  requestSignal.addEventListener('abort', handleAbort, { once: true });
  if (requestSignal.aborted) handleAbort();
  const timeoutId = setTimeout(() => {
    timedOut = true;
    controller.abort();
  }, timeoutMs);

  try {
    const token = await Promise.race([getValidToken(), abortPromise]);
    timings.tokenMs = elapsed();
    phase = 'response';

    if (!token) {
      throw new ApiError('Authentication required', { kind: 'auth', phase, timings });
    }

    const response = await Promise.race([fetch(url, {
      method,
      mode: 'cors',
      signal: requestSignal,
      headers: {
        accept: 'application/json',
        Authorization: `Bearer ${token}`,
      },
    }), abortPromise]);
    timings.responseMs = elapsed();
    phase = 'parse';

    if (response.status === 401 || response.status === 403) {
      throw new ApiError('Authentication failed', {
        status: response.status,
        kind: 'auth',
        phase,
        timings,
      });
    }

    if (!response.ok) {
      throw new ApiError('API request failed', {
        status: response.status,
        kind: 'http',
        phase,
        timings,
      });
    }

    try {
      const payload = await Promise.race([response.json(), abortPromise]);
      timings.totalMs = elapsed();
      return payload;
    } catch (error) {
      if (error instanceof ApiError) throw error;
      throw new ApiError('Invalid API response', { kind: 'parse', phase, timings });
    }
  } catch (error) {
    if (error instanceof ApiError) {
      reportSlowRequest(path, error);
      throw error;
    }

    if (error.name === 'AbortError') {
      const abortError = new ApiError(
        timedOut ? 'API request timed out' : 'API request cancelled',
        {
          kind: timedOut ? 'timeout' : 'cancelled',
          phase,
          timings: { ...timings, totalMs: elapsed() },
        }
      );
      reportSlowRequest(path, abortError);
      throw abortError;
    }

    throw new ApiError('Network request failed', {
      kind: 'network',
      phase,
      timings: { ...timings, totalMs: elapsed() },
    });
  } finally {
    clearTimeout(timeoutId);
    requestSignal.removeEventListener('abort', handleAbort);
    signal?.removeEventListener('abort', abortFromCaller);
  }
}
